import {
    Fragment,
    memo,
    useEffect,
    useId,
    useMemo,
    type HTMLAttributes,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    type Ref,
} from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
// eslint-disable-next-line no-restricted-imports
import { Select as SelectBase } from "@base-ui/react/select";
// eslint-disable-next-line no-restricted-imports
import type { BaseUIEvent } from "@base-ui/react/types";
import { styleWithVars } from "@lib/cssVars";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { unsupportedPickerKind, usePickerRoot } from "./PickerContext";
import { usePickerItemActivation } from "./usePickerItemActivation";
import { PickerItemIndicator } from "./PickerItemIndicator";
import {
    isRowNestedControlEvent,
    skipBaseUIForNestedControl,
} from "./PickerItemAction";
import { preventBaseUIHandler } from "./usePickerLevelKeyboard";
import { usePickerRowScrollGuardRef } from "./usePickerRowScrollGuard";
import {
    PickerItemContent,
    PickerTreeConnectors,
    pickerItemClassName,
} from "./PickerItemContent";
import type { PickerItemProps } from "./Picker.types";

/** A flyout row is an option that opens a listbox, mirroring tree rows' aria-expanded. */
const flyoutAriaProps = (
    isOpen: boolean,
): { "aria-haspopup": "listbox"; "aria-expanded": boolean } => ({
    "aria-haspopup": "listbox",
    "aria-expanded": isOpen,
});

/**
 * A disabled row's reason wins over its tooltip; `key` changes whenever the
 * row switches between the two, so the row's tooltip remounts.
 */
const resolveRowTooltip = ({
    isDisabled,
    disabledReason,
    tooltip,
}: {
    isDisabled: boolean;
    disabledReason: ReactNode;
    tooltip: ReactNode;
}): { content: ReactNode; key: string } => {
    if (isDisabled && disabledReason != null) {
        return { content: disabledReason, key: "disabled-reason" };
    }
    return { content: tooltip, key: tooltip == null ? "none" : "tooltip" };
};

/** A boolean row flag as a present-or-absent data attribute value. */
const presence = (isSet: boolean): "" | undefined => (isSet ? "" : undefined);

/** ArrowRight expands a collapsed row and ArrowLeft collapses an expanded one. */
const expandedAfterKey = ({
    key,
    expanded,
}: {
    key: string;
    expanded: boolean;
}): boolean | undefined => {
    if (key === "ArrowRight" && !expanded) {
        return true;
    }
    if (key === "ArrowLeft" && expanded) {
        return false;
    }
    return undefined;
};

const PickerItemImpl = ({
    value,
    children,
    leading,
    labelHint,
    wrap = false,
    description,
    rightText,
    trailing,
    destructive = false,
    disabled = false,
    disabledReason,
    tooltip,
    depth = 0,
    treePosition,
    ancestorLines,
    flyout = false,
    expandable = false,
    expanded = false,
    onExpandedChange,
    onClick,
    onSelect: onSelectProp,
    closeOnSelect: closeOnSelectProp = true,
    className,
    "data-testid": dataTestid,
    "data-forced-state": forcedState,
}: PickerItemProps) => {
    const root = usePickerRoot();
    const { cascade, flyoutRow, selectableFlyoutRow, onSelect, closeOnSelect } =
        usePickerItemActivation({
            value,
            onSelect: onSelectProp,
            closeOnSelect: closeOnSelectProp,
        });
    const isFlyout = flyout || flyoutRow !== null;
    // Base UI treats a `null` item value as matching an empty selection and
    // highlights it on open, so action rows carry a private value instead.
    const actionValue = `picker-action-${useId()}`;
    const isActionItem = onSelect !== undefined;
    const isSelectable = value !== undefined && !isActionItem;
    const isSelected = isSelectable && root.isSelected(value);
    const isDisabled = disabled || root.isDisabled;
    const label = typeof children === "string" ? children : undefined;
    const { content: rowTooltip, key: rowTooltipKey } = resolveRowTooltip({
        isDisabled,
        disabledReason,
        tooltip,
    });
    const { registerItem } = root;
    const guardRowScroll = usePickerRowScrollGuardRef();
    const setAnchor = flyoutRow?.setAnchor;
    const rowRef = useMemo(
        () => mergeRefs([setAnchor, guardRowScroll]),
        [setAnchor, guardRowScroll],
    );

    // Every valued row registers its label, selectable or not, so a chain or
    // free-text root can name a leaf it never rendered itself.
    useEffect(() => {
        if (value !== undefined && !isActionItem) {
            registerItem({ value, label, isDisabled: disabled });
        }
    }, [registerItem, isActionItem, value, label, disabled]);

    const handleClick = (event: BaseUIEvent<MouseEvent<HTMLDivElement>>) => {
        if (isRowNestedControlEvent(event)) {
            event.preventBaseUIHandler();
            return;
        }
        onClick?.(event);
        if (onSelect === undefined) {
            return;
        }
        event.preventBaseUIHandler();
        if (isDisabled) {
            return;
        }
        onSelect();
        if (closeOnSelect) {
            root.closePopup();
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (isRowNestedControlEvent(event)) {
            preventBaseUIHandler(event);
            return;
        }
        const nextExpanded = expandable
            ? expandedAfterKey({ key: event.key, expanded })
            : undefined;
        if (nextExpanded !== undefined) {
            event.preventDefault();
            onExpandedChange?.(nextExpanded);
        }
    };

    const content = (
        <>
            {depth > 0 && treePosition !== undefined && (
                <PickerTreeConnectors
                    depth={depth}
                    treePosition={treePosition}
                    ancestorLines={ancestorLines}
                />
            )}
            <PickerItemContent
                leading={leading}
                labelHint={labelHint}
                description={description}
                rightText={rightText}
                trailing={trailing}
                indicator={
                    <PickerItemIndicator
                        value={value}
                        isSelectable={isSelectable}
                        isSelected={isSelected}
                        isDisabled={isDisabled}
                        cascadeState={cascade?.state}
                    />
                }
                isFlyout={isFlyout}
                flyoutOpen={
                    selectableFlyoutRow === null
                        ? undefined
                        : {
                              label,
                              isOpen: selectableFlyoutRow.isOpen,
                              onOpen: selectableFlyoutRow.open,
                          }
                }
                expandable={expandable}
                expanded={expanded}
                onExpandedChange={onExpandedChange}
                hasLabelTooltip={rowTooltip == null && !wrap}
            >
                {children}
            </PickerItemContent>
        </>
    );

    const sharedProps = {
        disabled,
        className: pickerItemClassName({
            hasDescription: description !== undefined,
            isDestructive: destructive,
            rootClassNames: root.classNames,
            className,
        }),
        style: styleWithVars({ "--_picker-depth": depth }),
        "data-tree-position": depth > 0 ? treePosition : undefined,
        "data-action-item": presence(isActionItem),
        "data-wrap": presence(wrap),
        "data-destructive": presence(destructive),
        "data-flyout-open": presence(flyoutRow?.isOpen === true),
        "data-forced-state": forcedState,
        "data-flyout-id": flyoutRow?.id,
        ...(flyoutRow === null ? {} : flyoutAriaProps(flyoutRow.isOpen)),
        "data-testid": dataTestid,
        ref: rowRef,
        // Presses from a `Picker.ItemAction` (or a portal the row renders)
        // skip Base UI's row handlers: the pointerdown focus guard and the
        // mouseup / click commits. `PickerList` and `PickerPopup` handle the
        // action's keys and focus.
        onPointerDownCapture: skipBaseUIForNestedControl,
        onPointerDown: skipBaseUIForNestedControl,
        onMouseDown: skipBaseUIForNestedControl,
        onMouseUp: skipBaseUIForNestedControl,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        onPointerEnter: flyoutRow?.onPointerEnter,
        onPointerLeave: flyoutRow?.onPointerLeave,
        // The tooltip wraps the element Base UI renders, not the item: the
        // item's `disabled` prop would make Tooltip wrap the row in a span,
        // while the rendered div only carries `aria-disabled` and still
        // receives hover. It opens beside the row so it never covers the
        // neighbouring rows (Menu rows do the same).
        render: (
            rowProps: HTMLAttributes<HTMLDivElement> & {
                ref?: Ref<HTMLDivElement>;
            },
        ) => (
            <Tooltip content={rowTooltip} side="inline-start">
                <div {...rowProps} />
            </Tooltip>
        ),
    };

    const renderRow = () => {
        switch (root.kind) {
            case "combobox":
                return (
                    <ComboboxBase.Item
                        {...sharedProps}
                        // Combobox rows are not focusable by default; a flyout row
                        // takes focus back when its level closes.
                        tabIndex={flyoutRow === null ? undefined : -1}
                        value={value ?? actionValue}
                    >
                        {content}
                    </ComboboxBase.Item>
                );
            case "select":
                return (
                    <SelectBase.Item
                        {...sharedProps}
                        value={value ?? actionValue}
                        label={label}
                    >
                        {content}
                    </SelectBase.Item>
                );
            default:
                return unsupportedPickerKind(root.kind);
        }
    };

    return <Fragment key={rowTooltipKey}>{renderRow()}</Fragment>;
};

/**
 * One row of the popup, rendered as the active root kind's Base UI item.
 * Draws the selected check (single) or checkbox (multiple) from root state;
 * `onSelect` makes it an action row that never becomes the root value and
 * closes the popup unless `closeOnSelect` is false; `tooltip` shows over the
 * whole row on hover, and a disabled row's `disabledReason` takes its place.
 * Memoised: data-driven
 * rows receive stable props, so typing in the root's input (which only moves
 * the query context) does not re-render them.
 */
export const PickerItem = memo(PickerItemImpl);
